import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Table, TableHead, TableRow,
  TableCell, TableBody, Divider, Button, CircularProgress, Alert,
} from '@mui/material';
import { Print, ArrowBack } from '@mui/icons-material';
import { prescriptionService, printSettingsService, translateService } from '../../services/api';
import { getLang, getT, formatDurationLocalized, dateLocale, translateGender, translateMealPref } from '../../utils/translations';

const PrescriptionPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [translated, setTranslated] = useState({});
  const [translateError, setTranslateError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [prescRes, psRes] = await Promise.all([
          prescriptionService.getPrescriptionById(id),
          printSettingsService.getSettings(),
        ]);
        const rx = prescRes.data;
        setPrescription(rx);
        setPrintSettings(psRes.data);

        const lang = getLang(rx.patient);
        if (lang !== 'en') {
          await fetchTranslations(rx, lang);
        }
      } catch {
        setError('Prescription not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const fetchTranslations = async (rx, lang) => {
    const patient = rx.patient;
    const fields = {
      patientName: `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim(),
      chiefComplaint: rx.chiefComplaint || '',
      provisionalDiagnosis: rx.provisionalDiagnosis || '',
      allergy: rx.allergy || '',
      medicalNotes: rx.medicalNotes || '',
      referDoctorName: rx.referDoctorName || '',
    };

    const keys = Object.keys(fields).filter(k => fields[k]);
    const texts = keys.map(k => fields[k]);

    if (texts.length === 0) return;

    try {
      const res = await translateService.translate(texts, lang);
      const map = {};
      keys.forEach((k, i) => { map[k] = res.data.translations[i] || fields[k]; });
      setTranslated(map);
    } catch (err) {
      console.error('Translation API error:', err?.response?.data || err?.message || err);
      setTranslateError(true);
    }
  };

  // Auto-print once data and translations are ready
  useEffect(() => {
    if (!loading && prescription) {
      const timer = setTimeout(() => window.print(), 600);
      return () => clearTimeout(timer);
    }
  }, [loading, prescription]);

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
    ) age--;
    return age;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !prescription) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>{error || 'Prescription not found.'}</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  const patient  = prescription.patient;
  const doctor   = prescription.doctor;
  const lang     = getLang(patient);
  const t        = getT(patient);
  const locale   = dateLocale(lang);

  const mt = printSettings?.marginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? 10;
  const isPrePrinted = printSettings?.letterheadType === 'Pre-Printed Letterhead';

  const medicines      = prescription.prescriptionMedicines      || [];
  const advices        = prescription.prescriptionAdvices        || [];
  const investigations = prescription.prescriptionInvestigations || [];

  const patientName    = translated.patientName    || `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`;
  const chiefComplaint = translated.chiefComplaint || prescription.chiefComplaint;
  const diagnosis      = translated.provisionalDiagnosis || prescription.provisionalDiagnosis;
  const allergy        = translated.allergy        || prescription.allergy;
  const medicalNotes   = translated.medicalNotes   || prescription.medicalNotes;
  const referName      = translated.referDoctorName || prescription.referDoctorName;

  const doctorDisplayName = lang === 'mr' && doctor?.marathiName
    ? doctor.marathiName
    : lang === 'hi' && doctor?.hindiName
      ? doctor.hindiName
      : `Dr. ${doctor?.firstName ?? ''} ${doctor?.lastName ?? ''}`;

  return (
    <Box>
      {/* On-screen controls — hidden during print */}
      <Box className="no-print" sx={{ p: 2, display: 'flex', gap: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
        <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button startIcon={<Print />} variant="contained" onClick={() => window.print()}>
          Print Again
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
          Rx #{prescription.id} — {patient?.firstName} {patient?.lastName}
          {lang !== 'en' && <span style={{ marginLeft: 8, color: '#1565c0', fontWeight: 600 }}>
            [{lang === 'hi' ? 'हिंदी' : 'मराठी'}]
          </span>}
        </Typography>
      </Box>

      {/* Translation error warning — screen only */}
      {translateError && (
        <Box className="no-print" sx={{ maxWidth: 800, mx: 'auto', px: 3, pt: 1 }}>
          <Alert severity="warning" onClose={() => setTranslateError(false)}>
            Translation unavailable — printing in English. Verify your Google Translate API key in appsettings.json and confirm Cloud Translation API is enabled in Google Cloud Console.
          </Alert>
        </Box>
      )}

      {/* Prescription Content */}
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }} className="print-area">

        {/* Hospital Header */}
        {!isPrePrinted && (
          <Box sx={{
            background: 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
            color: '#fff',
            textAlign: 'center',
            py: 1.5,
            px: 2,
            mb: 1.5,
            mx: -3,
            mt: -3,
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', letterSpacing: 1, lineHeight: 1.2 }}>
              {printSettings?.hospitalName || 'Vedanta Hospital'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', opacity: 0.9, mt: 0.3 }}>
              {printSettings?.address || ''}
              {printSettings?.address && printSettings?.phone ? ' | ' : ''}
              {printSettings?.phone || ''}
            </Typography>
          </Box>
        )}

        {/* Patient Info */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 1, mb: 1.5 }}>
          <Grid container alignItems="flex-start">
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>{t.patient}:</strong> {patientName}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>{t.age}:</strong> {calculateAge(patient?.dateOfBirth)} {t.yrs}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>{t.gender}:</strong> {translateGender(patient?.gender, lang)}
              </Typography>
              {prescription.weight && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>{t.weight}:</strong> {prescription.weight} kg
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              {patient?.bloodGroup && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>{t.bloodGroup}:</strong> {patient.bloodGroup}
                </Typography>
              )}
              {prescription.bp && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>{t.bp}:</strong> {prescription.bp} mmHg
                </Typography>
              )}
              {prescription.pulse && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>{t.pulse}:</strong> {prescription.pulse} bpm
                </Typography>
              )}
              {(prescription.spo2 || prescription.spO2) && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>{t.spo2}:</strong> {prescription.spo2 || prescription.spO2}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>{t.rxNo}:</strong> #{prescription.id}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>{t.date}:</strong> {new Date(prescription.date).toLocaleDateString(locale)}
              </Typography>
              {prescription.gestationalAge && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>{t.gestationalAge}:</strong> {prescription.gestationalAge}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Chief Complaint */}
        {chiefComplaint && (
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 0.75, mb: 1.5 }}>
            <Typography variant="body2">
              <strong>{t.chiefComplaint}:</strong> {chiefComplaint}
            </Typography>
          </Box>
        )}

        {/* Diagnosis + Allergy */}
        {(diagnosis || allergy) && (
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 0.75, mb: 1.5, display: 'flex', gap: 3 }}>
            {diagnosis && (
              <Typography variant="body2" sx={{ flex: 1 }}>
                <strong>{t.provisionalDiagnosis}:</strong> {diagnosis}
              </Typography>
            )}
            {allergy && (
              <Typography variant="body2" sx={{ flex: 1 }}>
                <strong>{t.allergy}:</strong> {allergy}
              </Typography>
            )}
          </Box>
        )}

        {/* Medicines */}
        {medicines.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                {t.medicinesPrescribed}
              </Typography>
            </Box>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.82rem' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                  <TableCell sx={{ fontWeight: 700, width: '40%' }}>{t.medicine}</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>{t.dosage}</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>{t.duration}</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>{t.meal}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medicines.map((pm, idx) => (
                  <TableRow key={pm.id} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                        {lang === 'mr' && pm.medicine?.marathiName
                          ? pm.medicine.marathiName
                          : lang !== 'en' && pm.medicine?.hindiName
                            ? pm.medicine.hindiName
                            : pm.medicine?.name}
                      </Typography>
                      {pm.medicine?.composition && pm.medicine.composition !== pm.medicine.name && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#666', fontStyle: 'italic' }}>
                          {pm.medicine.composition}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{pm.dosage}</TableCell>
                    <TableCell>{formatDurationLocalized(pm.duration, t)}</TableCell>
                    <TableCell>{translateMealPref(pm.mealPref, lang) || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Advices + Investigations */}
        {(advices.length > 0 || investigations.length > 0) && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {advices.length > 0 && (
              <Grid item xs={investigations.length > 0 ? 6 : 12}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    {t.adviceGiven}
                  </Typography>
                </Box>
                {advices.map((pa) => (
                  <Typography key={pa.id} sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                    • {lang === 'mr' && pa.advice?.marathiText
                      ? pa.advice.marathiText
                      : lang !== 'en' && pa.advice?.hindiText
                        ? pa.advice.hindiText
                        : pa.advice?.text}
                  </Typography>
                ))}
              </Grid>
            )}
            {investigations.length > 0 && (
              <Grid item xs={advices.length > 0 ? 6 : 12}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    {t.investigationsAdvised}
                  </Typography>
                </Box>
                {investigations.map((pi) => (
                  <Typography key={pi.id} sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                    • {pi.investigation?.name}
                  </Typography>
                ))}
              </Grid>
            )}
          </Grid>
        )}

        {/* Medical Notes */}
        {medicalNotes && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                {t.medicalNotes}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
              {medicalNotes}
            </Typography>
          </Box>
        )}

        {/* Follow-up + Refer Doctor */}
        {(prescription.followUpDate || referName) && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 4 }}>
            {prescription.followUpDate && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    {t.followUpDate}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                  {new Date(prescription.followUpDate).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            )}
            {referName && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    {t.referToDoctor}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                  {referName}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 1.5, borderColor: '#1565c0' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
              {t.computerGenerated}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {doctorDisplayName}
              </Typography>
              {doctor?.education && (
                <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>{doctor.education}</Typography>
              )}
              {doctor?.medicalRegistrationNumber && (
                <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                  {t.regNo}: {doctor.medicalRegistrationNumber}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.82rem', mt: 6, borderTop: '1px solid #555', pt: 0.5, minWidth: 160, textAlign: 'center' }}>
                {t.signature}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <style>{`
        @media print {
          @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; }
          .no-print { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default PrescriptionPrintPage;
